// PetroTrack business calculations — SINGLE SOURCE OF TRUTH (web + mobile).
// Mirrors docs/02-BUSINESS_LOGIC.md + docs/16-ENTRY_REMODEL_PLAN.md.
//
// Reconciliation is PER PRODUCT per day (all tanks + all pumps of a product),
// because pumps draw from any tank of that product. Money that depends on
// station-level costs — expenses, net profit, amount to remit — is computed at
// the STATION/day level (summing products), so expenses are never double-counted.

import type { ProductDayEntry, PumpReading, Expense, ExcessStatus, TankLevel } from './types';

export const CAN_SIZE_L = 20;
export const LEGAL_MIN_EXCESS = 1.0;
export const LEGAL_MAX_EXCESS = 1.5;

// ── per-pump / per-tank ─────────────────────────────────────────────────────
export const pumpLitres = (p: PumpReading): number =>
  p.closingMeter != null && p.openingMeter != null ? p.closingMeter - p.openingMeter : 0;

export const tankLitres = (t: TankLevel): number =>
  t.closingLevel == null ? 0 : t.openingLevel + t.supplyReceived - t.closingLevel;

// ── PRODUCT-level (one ProductDayEntry = all tanks + pumps of a product) ─────
/** Pump Sales (litres) = Σ over the product's pumps. */
export const calcPumpSales = (e: ProductDayEntry): number => e.pumps.reduce((s, p) => s + pumpLitres(p), 0);

/** Tank Sales (litres) = Σ over the product's tanks (opening + supply − closing). */
export const calcTankSales = (e: ProductDayEntry): number => e.tanks.reduce((s, t) => s + tankLitres(t), 0);

/** Measured Excess (litres) = Pump Sales − Tank Sales. Positive = favorable. */
export const calcMeasuredExcess = (e: ProductDayEntry): number => calcPumpSales(e) - calcTankSales(e);

/** Expected Excess (litres) from the per-pump calibration. */
export const calcExpectedExcess = (e: ProductDayEntry): number =>
  e.pumps.reduce((s, p) => s + (pumpLitres(p) / CAN_SIZE_L) * p.excessPer20L, 0);

/** Excess Gap (litres) = Measured − Expected. */
export const calcExcessGap = (e: ProductDayEntry): number => calcMeasuredExcess(e) - calcExpectedExcess(e);

/** Maximum legally-allowed excess (1.5 L per 20 L) for the day's litres. */
export const calcLegalMaxExcess = (e: ProductDayEntry): number =>
  e.pumps.reduce((s, p) => s + (pumpLitres(p) / CAN_SIZE_L) * LEGAL_MAX_EXCESS, 0);

export const calcExcessStatus = (e: ProductDayEntry): ExcessStatus => {
  const measured = calcMeasuredExcess(e);
  if (measured < calcExpectedExcess(e)) return 'shortfall';
  if (measured > calcLegalMaxExcess(e)) return 'over_limit';
  return 'ok';
};

/** Revenue (₦) = Pump Sales × price. */
export const calcRevenue = (e: ProductDayEntry): number =>
  e.pricePerLiter == null ? 0 : calcPumpSales(e) * e.pricePerLiter;

/** Measured Excess Profit (₦) — the money. */
export const calcMeasuredExcessProfit = (e: ProductDayEntry): number =>
  e.pricePerLiter == null ? 0 : calcMeasuredExcess(e) * e.pricePerLiter;

/** Expected Excess Profit (₦) — admin display only. */
export const calcExpectedExcessProfit = (e: ProductDayEntry): number =>
  e.pricePerLiter == null ? 0 : calcExpectedExcess(e) * e.pricePerLiter;

export interface ProductMetrics {
  product: string;
  pumpSales: number;
  tankSales: number;
  measuredExcess: number;
  expectedExcess: number;
  revenue: number;
  measuredExcessProfit: number;
  expectedExcessProfit: number;
  status: ExcessStatus;
}

export function productMetrics(e: ProductDayEntry): ProductMetrics {
  return {
    product: e.product,
    pumpSales: calcPumpSales(e),
    tankSales: calcTankSales(e),
    measuredExcess: calcMeasuredExcess(e),
    expectedExcess: calcExpectedExcess(e),
    revenue: calcRevenue(e),
    measuredExcessProfit: calcMeasuredExcessProfit(e),
    expectedExcessProfit: calcExpectedExcessProfit(e),
    status: calcExcessStatus(e),
  };
}

// ── STATION/day-level (across products + station expenses) ───────────────────
export const calcTotalExpenses = (expenses: Expense[]): number => expenses.reduce((s, x) => s + x.amount, 0);

export interface StationMetrics {
  revenue: number;
  measuredExcessProfit: number;
  expectedExcessProfit: number;
  pumpSales: number;
  tankSales: number;
  measuredExcess: number;
  expectedExcess: number;
  totalExpenses: number;
  netProfit: number;      // revenue − expenses (operating)
  amountToRemit: number;  // revenue + measured excess profit − expenses
  entryCount: number;
}

/** Aggregate product entries (any number of products / days) + station expenses
 *  into one station-level summary. Expenses are applied ONCE here. */
export function aggregateMetrics(entries: ProductDayEntry[], expenses: Expense[] = []): StationMetrics {
  const base = entries.reduce(
    (acc, e) => ({
      revenue: acc.revenue + calcRevenue(e),
      measuredExcessProfit: acc.measuredExcessProfit + calcMeasuredExcessProfit(e),
      expectedExcessProfit: acc.expectedExcessProfit + calcExpectedExcessProfit(e),
      pumpSales: acc.pumpSales + calcPumpSales(e),
      tankSales: acc.tankSales + calcTankSales(e),
      measuredExcess: acc.measuredExcess + calcMeasuredExcess(e),
      expectedExcess: acc.expectedExcess + calcExpectedExcess(e),
    }),
    { revenue: 0, measuredExcessProfit: 0, expectedExcessProfit: 0, pumpSales: 0, tankSales: 0, measuredExcess: 0, expectedExcess: 0 },
  );
  const totalExpenses = calcTotalExpenses(expenses);
  return {
    ...base,
    totalExpenses,
    netProfit: base.revenue - totalExpenses,
    amountToRemit: base.revenue + base.measuredExcessProfit - totalExpenses,
    entryCount: entries.length,
  };
}
